import React from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Thread type configurations with 1G2N colors
 */
const THREAD_TYPES = {
  photo_voice: {
    icon: '📸',
    label: 'Photo Story',
    color: 'bg-fb-olive',
    borderColor: 'border-fb-olive',
    lightBg: 'bg-fb-cream',
  },
  video_wisdom: {
    icon: '🎬',
    label: 'Video Wisdom',
    color: 'bg-thread-video',
    borderColor: 'border-thread-video',
    lightBg: 'bg-green-50',
  },
  then_now: {
    icon: '📸',
    label: 'Then & Now',
    color: 'bg-thread-compare',
    borderColor: 'border-thread-compare',
    lightBg: 'bg-amber-50',
  },
  artifact: {
    icon: '🏺',
    label: 'Artifact',
    color: 'bg-thread-artifact',
    borderColor: 'border-thread-artifact',
    lightBg: 'bg-stone-50',
  },
  memory_layers: {
    icon: '💬',
    label: 'Memory Layers',
    color: 'bg-fb-olive',
    borderColor: 'border-fb-olive',
    lightBg: 'bg-fb-cream',
  },
  time_capsule: {
    icon: '⏰',
    label: 'Time Capsule',
    color: 'bg-thread-capsule',
    borderColor: 'border-thread-capsule',
    lightBg: 'bg-orange-50',
  },
}

/**
 * WisdomThreadCard Component
 *
 * Displays a wisdom thread in the Family Board with visual thread lines
 * connecting question to answer.
 */
function WisdomThreadCard({
  thread,
  onAction,
  isWaiting = false,
}) {
  const navigate = useNavigate()
  const typeConfig = THREAD_TYPES[thread.threadType] || THREAD_TYPES.photo_voice

  return (
    <div
      className={`
        bg-white rounded-xl overflow-hidden shadow-sm
        border-2
        ${isWaiting ? 'border-thread-waiting' : typeConfig.borderColor}
      `}
    >
      {/* Thread Header Banner - Shows story preview */}
      <div
        className={`
          px-4 py-3
          ${isWaiting ? 'bg-thread-waiting' : typeConfig.color}
        `}
      >
        <p className="font-serif font-bold text-white text-sm line-clamp-2">
          {thread.question || thread.preview || 'A family story'}
          {isWaiting && <span className="font-normal"> • Your turn to respond</span>}
        </p>
      </div>

      {/* Content area - thumbnail + details */}
      <div className="px-4 py-4 flex gap-4">
        {/* Thumbnail or media type indicator */}
        {thread.mediaPreview?.thumbnail ? (
          <div className="w-28 rounded-lg overflow-hidden bg-fb-cream flex-shrink-0 relative">
            {thread.hasVideo ? (
              <video
                src={`${thread.mediaPreview.thumbnail}#t=0.5`}
                className="w-full object-cover"
                style={{ minHeight: '120px', maxHeight: '180px' }}
                muted
                playsInline
                preload="auto"
              />
            ) : (
              <img
                src={thread.mediaPreview.thumbnail}
                alt=""
                className="w-full object-cover"
                style={{ minHeight: '120px', maxHeight: '180px' }}
              />
            )}
            {thread.hasVideo && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <span className="text-white text-2xl">▶️</span>
              </div>
            )}
          </div>
        ) : (thread.hasAudio || thread.hasVideo) ? (
          <div className="w-28 h-20 rounded-lg bg-fb-cream flex-shrink-0 flex flex-col items-center justify-center">
            {thread.hasVideo ? (
              <>
                <span className="text-3xl mb-1">🎬</span>
                <span className="text-xs text-gray-500">Video</span>
              </>
            ) : thread.hasAudio ? (
              <>
                <span className="text-3xl mb-1">🎙️</span>
                <span className="text-xs text-gray-500">
                  {thread.audioDuration || 'Audio'}
                </span>
              </>
            ) : null}
          </div>
        ) : null}

        {/* Details */}
        <div className="flex-1 min-w-0">
          {/* Sharer info */}
          <div className="flex items-center gap-2 mb-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${thread.asker?.id ? 'cursor-pointer hover:ring-2 hover:ring-fb-olive' : ''}`}
              style={{ backgroundColor: thread.asker?.avatarColor || '#8B9D4A' }}
              onClick={thread.asker?.id ? () => navigate(`/family-hub/member/${thread.asker.id}`) : undefined}
            >
              {thread.asker?.initials || '?'}
            </div>
            <div>
              {thread.asker?.id ? (
                <span
                  className="font-serif font-semibold text-gray-900 text-sm cursor-pointer hover:underline"
                  onClick={() => navigate(`/family-hub/member/${thread.asker.id}`)}
                >
                  {thread.asker?.name || 'Family Member'}
                </span>
              ) : (
                <span className="font-serif font-semibold text-gray-900 text-sm">
                  {thread.asker?.name || 'Family Member'}
                </span>
              )}
              <span className="text-gray-400 text-xs ml-1">
                {thread.asker?.action || 'shared'}
              </span>
              <p className="font-serif text-gray-500 text-xs">
                {thread.timeAgo}
              </p>
            </div>
          </div>

          {/* Date and Location */}
          {(thread.date || thread.location) && (
            <div className="font-serif text-gray-500 text-xs flex items-center gap-2 flex-wrap">
              {thread.date && (
                <span className="flex items-center gap-1">
                  <span>📅</span>
                  <span>{thread.date}</span>
                </span>
              )}
              {thread.date && thread.location && <span>·</span>}
              {thread.location && (
                <span className="flex items-center gap-1">
                  <span>📍</span>
                  <span>{thread.location}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Response area or existing responses */}
      <div className="px-4 pb-4">
        {isWaiting ? (
          <WaitingResponsePrompt
            thread={thread}
            typeConfig={typeConfig}
            onAction={onAction}
          />
        ) : thread.responses && thread.responses.length > 0 ? (
          <ThreadResponses responses={thread.responses} />
        ) : null}
      </div>

      {/* Last Comment */}
      {thread.lastComment && (
        <div className="mx-4 mb-3 bg-fb-cream rounded-lg p-3">
          <div className="flex items-start gap-2">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
              style={{ backgroundColor: '#556B2F', fontSize: '10px' }}
            >
              {thread.lastComment.authorInitial || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-serif font-semibold text-gray-800" style={{ fontSize: '12px' }}>
                  {thread.lastComment.authorName}
                </span>
                <span className="font-serif text-gray-400" style={{ fontSize: '11px' }}>
                  {thread.lastComment.timeAgo}
                </span>
              </div>
              <p className="font-serif text-gray-600 line-clamp-2" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                "{thread.lastComment.text}"
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action button + Engagement footer */}
      <div className="px-4 pb-4 flex items-center gap-4">
        {!isWaiting && (
          <button
            onClick={() => {
              const storyId = thread.id.replace('story-', '').replace('question-', '')
              navigate(`/stories/${storyId}`)
            }}
            className="bg-fb-olive hover:bg-fb-olive/90 text-white font-bold py-2.5 px-5 rounded-lg text-sm transition-colors min-h-[40px]"
          >
            View & Comment
          </button>
        )}
        {thread.engagement && (
          <span className="font-serif text-xs text-gray-600">
            {thread.engagement}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * WaitingResponsePrompt - Shows when thread is awaiting response
 */
function WaitingResponsePrompt({ thread, typeConfig, onAction }) {
  const getPromptText = () => {
    switch (thread.threadType) {
      case 'photo_voice':
        return { main: 'Record the story behind this photo', sub: 'Your family is waiting to hear your voice' }
      case 'video_wisdom':
        return { main: 'Share your wisdom in a video', sub: 'Record a moment of wisdom' }
      case 'artifact':
        return { main: 'Tell the story of this artifact', sub: 'Share its history and meaning' }
      case 'memory_layers':
        return { main: 'Add your memory of this moment', sub: 'What do you remember?' }
      default:
        return { main: 'Share your response', sub: 'Your family is waiting' }
    }
  }

  const prompt = getPromptText()

  return (
    <div className="flex items-start gap-3">
      {/* Your avatar */}
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-full bg-fb-olive border-2 border-thread-waiting flex items-center justify-center text-white font-bold text-sm">
          You
        </div>
      </div>

      {/* Response prompt */}
      <div
        className="flex-1 border-2 border-dashed border-thread-waiting rounded-lg p-4 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors"
        onClick={() => onAction && onAction(thread)}
      >
        <p className="font-serif font-semibold text-thread-waiting text-sm text-center">
          🎙️ {prompt.main}
        </p>
        <p className="font-serif text-gray-500 text-xs text-center mt-1">
          {prompt.sub}
        </p>
        <div className="flex justify-center mt-3">
          <button className="bg-thread-waiting hover:bg-thread-waiting/90 text-white font-serif font-semibold text-xs px-4 py-2 rounded-lg transition-colors">
            Start Recording
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * ThreadResponses - Shows existing responses in a thread
 */
function ThreadResponses({ responses }) {
  return (
    <div className="space-y-3">
      {responses.slice(0, 2).map((response, idx) => (
        <div key={idx} className="flex items-start gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
            style={{ backgroundColor: response.avatarColor || '#556B2F' }}
          >
            {response.initials || '?'}
          </div>
          <div className="flex-1 bg-fb-cream rounded-lg p-3">
            <p className="font-serif font-semibold text-gray-900 text-xs">
              {response.name}
            </p>
            <p className="font-serif text-gray-600 text-xs mt-1 line-clamp-2">
              {response.preview}
            </p>
            {response.hasAudio && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-fb-olive">🎙️</span>
                <span className="text-xs text-gray-500">{response.duration}</span>
              </div>
            )}
          </div>
        </div>
      ))}
      {responses.length > 2 && (
        <p className="font-serif text-xs text-fb-olive text-center">
          +{responses.length - 2} more responses
        </p>
      )}
    </div>
  )
}

export default WisdomThreadCard
export { THREAD_TYPES }
